// QuickUpdateCard — one-stop card that lives at the top of every job page
// (both admin and worker views). Solves the "hunt through tabs to update a
// job" UX by putting photo/file upload, notes, and status change side-by-
// side in a single card.
//
// Wired to:
//   • POST  /api/jobs/{id}/photo                — image upload (via prop uploadPhoto if provided)
//   • POST  /api/jobs/{id}/photo   (kind=receipt) — PDF/doc upload
//   • POST  /api/jobs/{id}/notes                — appends a field note
//   • PATCH /api/jobs/{id}                      — status transition
import { useRef, useState } from "react";
import { Camera, FilePdf, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function QuickUpdateCard({ job, onReload, uploadPhoto, disabled }) {
  const [note, setNote] = useState("");
  const [busyNote, setBusyNote] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [photoKind, setPhotoKind] = useState("after");
  const photoRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusyNote(true);
    try {
      await api.post(`/jobs/${job.id}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Note added");
      onReload && onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't add note")); }
    finally { setBusyNote(false); }
  };

  const setStatus = async (next) => {
    if (job.status === next) return;
    setBusyStatus(true);
    try {
      if (next === "completed" || next === "follow_up") {
        // Route terminal statuses through /complete so timestamps fire
        // and (for workers) gates run. Admin caller passes empty notes.
        await api.post(`/jobs/${job.id}/complete`, {
          status: next,
          notes: "",
          follow_up_reason: next === "follow_up" ? "other" : undefined,
        });
      } else {
        await api.patch(`/jobs/${job.id}`, { status: next });
      }
      toast.success(`Status → ${next.replace("_", " ")}`);
      onReload && onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail?.message ?? e?.response?.data?.detail ?? "Couldn't change status")); }
    finally { setBusyStatus(false); }
  };

  // Image upload — prefer the caller's uploadPhoto (which compresses) when
  // provided, else fall back to a plain multipart POST.
  const handlePhotos = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      for (const f of arr) {
        // eslint-disable-next-line no-await-in-loop
        if (uploadPhoto) {
          await uploadPhoto(f, photoKind);
        } else {
          const fd = new FormData();
          fd.append("kind", photoKind);
          fd.append("file", f);
          await api.post(`/jobs/${job.id}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }
      toast.success(`${arr.length} photo${arr.length === 1 ? "" : "s"} attached as ${photoKind}`);
    } catch (e) { toast.error("Some photos failed to upload"); }
    finally { setUploading(false); onReload && onReload(); }
  };

  // Non-image attachments (PDFs, docs, quotes, invoices) always land in the
  // Receipts bucket — the existing catch-all for job documentation.
  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("kind", "receipt");
        fd.append("file", f);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/jobs/${job.id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${arr.length} file${arr.length === 1 ? "" : "s"} attached`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "File upload failed")); }
    finally { setUploading(false); onReload && onReload(); }
  };

  const photoCount = (job.before_photos || []).length + (job.after_photos || []).length;
  const fileCount  = (job.receipts || []).length;
  const noteCount  = (job.notes || []).length;

  return (
    <div className="mt-4 p-4 border-2 border-[#0f172a] bg-white shadow-[4px_4px_0_#00C4CC]"
      data-testid="job-quick-update-card">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
        <div className="font-black uppercase tracking-widest text-xs">Quick Update</div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          📸 {photoCount} · 📎 {fileCount} · 📝 {noteCount}
        </div>
      </div>

      {/* PHOTO / FILE ROW */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 shrink-0">Photo kind:</label>
          {["before", "after", "receipt"].map(k => (
            <button key={k} onClick={() => setPhotoKind(k)}
              data-testid={`quick-photo-kind-${k}`}
              className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 ${
                photoKind === k ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300 hover:bg-slate-100"
              }`}>
              {k}
            </button>
          ))}
        </div>
        <input ref={photoRef} type="file" accept="image/*,video/*" multiple className="hidden"
          onChange={(e) => { handlePhotos(e.target.files); e.target.value = ""; }}
          data-testid="quick-photo-input"/>
        <input ref={fileRef} type="file"
          accept="application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.eml,.msg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          multiple className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="quick-file-input"/>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => photoRef.current?.click()} disabled={disabled || uploading}
            data-testid="quick-photo-btn"
            className="h-12 border-2 border-dashed border-[#00C4CC] bg-cyan-50 hover:bg-cyan-100 font-black uppercase text-xs tracking-widest text-[#0e7490] disabled:opacity-40 flex items-center justify-center gap-2">
            <Camera weight="fill" size={16}/>
            {uploading ? "…" : `Photo → ${photoKind}`}
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={disabled || uploading}
            data-testid="quick-file-btn"
            className="h-12 border-2 border-dashed border-fuchsia-500 bg-fuchsia-50 hover:bg-fuchsia-100 font-black uppercase text-xs tracking-widest text-fuchsia-800 disabled:opacity-40 flex items-center justify-center gap-2">
            <FilePdf weight="fill" size={16}/>
            {uploading ? "…" : "File · PDF / doc"}
          </button>
        </div>
      </div>

      {/* NOTES ROW */}
      <div className="mb-3">
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Add a note</label>
        <textarea value={note} onChange={e => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
          rows={2} data-testid="quick-note-input"
          placeholder="What did you see / do? (⌘/Ctrl+Enter to save)"
          className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] text-sm resize-none"/>
        <button onClick={addNote} disabled={busyNote || !note.trim() || disabled}
          data-testid="quick-note-save"
          className="mt-2 h-10 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-2">
          <PaperPlaneTilt weight="fill" size={12}/> {busyNote ? "Saving…" : "Add note"}
        </button>
      </div>

      {/* STATUS ROW */}
      <div className="pt-2 border-t border-slate-100">
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-2">Change status</label>
        <div className="flex flex-wrap gap-1">
          {[
            ["assigned", "Assigned", "bg-blue-500"],
            ["in_progress", "In Progress", "bg-[#00C4CC]"],
            ["follow_up", "Follow-up", "bg-yellow-500"],
            ["completed", "Complete", "bg-green-600"],
          ].map(([val, label, color]) => (
            <button key={val} onClick={() => setStatus(val)}
              disabled={busyStatus || job.status === val || disabled}
              data-testid={`quick-status-${val}`}
              className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest text-white ${color} ${
                job.status === val ? "ring-2 ring-offset-1 ring-[#0f172a] opacity-100" : "opacity-70 hover:opacity-100"
              } disabled:cursor-not-allowed`}>
              {label}{job.status === val && " ✓"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
